import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db, storage, appId } from '../firebase';
import { doc, onSnapshot, collection, query, addDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import MessageBox from '../components/MessageBox';
import { BANKING_DETAILS } from '../utils/constants';

function FormsPage() {
    const { userDetails, currentUser } = useAuth();
    const [forms, setForms] = useState([]);
    const [termsText, setTermsText] = useState('');
    const [isEditingTerms, setIsEditingTerms] = useState(false);
    const [tempTermsText, setTempTermsText] = useState('');
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadName, setUploadName] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const [forceReset, setForceReset] = useState(false); 

    useEffect(() => {
        if (!db) return;
        const settingsRef = doc(db, 'artifacts', appId, 'public', 'appSettings');
        const unsubSettings = onSnapshot(settingsRef, (docSnap) => {
            if (docSnap.exists()) {
                setTermsText(docSnap.data().termsAndConditions || 'No terms and conditions currently set.');
            }
        });
        const formsColRef = collection(db, 'artifacts', appId, 'public', 'data', 'formDocuments');
        const q = query(formsColRef); 
        const unsubForms = onSnapshot(q, (snapshot) => {
            const fetchedForms = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedForms.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setForms(fetchedForms);
        });
        return () => { unsubSettings(); unsubForms(); };
    }, []);

    // ... (Handlers for Upload, Delete, Edit Terms - Copy from original code)
    const handleEditTerms = () => { setTempTermsText(termsText); setForceReset(false); setIsEditingTerms(true); };
    const handleCancelEditTerms = () => { setIsEditingTerms(false); setTempTermsText(''); setForceReset(false); };
    const handleSaveTerms = async () => {
        setLoading(true); setMessage('');
        try {
            const settingsRef = doc(db, 'artifacts', appId, 'public', 'appSettings');
            const updateData = { termsAndConditions: tempTermsText };
            if (forceReset) updateData.termsVersion = new Date();
            await setDoc(settingsRef, updateData, { merge: true });
            setMessage(forceReset ? 'Terms saved. All users will be forced to accept.' : 'Terms saved successfully.');
            setMessageType('info');
            setIsEditingTerms(false);
        } catch (error) { setMessage(error.message); setMessageType('error'); } finally { setLoading(false); }
    };
    
    const handleUploadForm = async (e) => {
        e.preventDefault();
        if (!uploadFile || !uploadName) { setMessage("Provide file and name."); setMessageType('error'); return; }
        setLoading(true);
        try {
            const fileName = `${Date.now()}_${uploadFile.name}`;
            const storageRef = ref(storage, `artifacts/${appId}/public/forms/${fileName}`);
            await uploadBytes(storageRef, uploadFile);
            const downloadURL = await getDownloadURL(storageRef);
            await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'formDocuments'), {
                name: uploadName, url: downloadURL, storagePath: `artifacts/${appId}/public/forms/${fileName}`,
                uploadedBy: currentUser.uid, uploadedAt: new Date()
            });
            setMessage('Uploaded.'); setMessageType('info'); setUploadFile(null); setUploadName('');
        } catch(e) { setMessage(e.message); setMessageType('error'); } finally { setLoading(false); }
    };

    const handleDeleteForm = async (form) => {
        if (!window.confirm("Delete?")) return;
        try {
            if (form.storagePath) await deleteObject(ref(storage, form.storagePath));
            await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'formDocuments', form.id));
        } catch(e) { console.error(e); }
    };

    return (
        <div className="container mx-auto p-6 bg-white rounded-lg shadow-lg my-6">
            <MessageBox message={message} type={messageType} onClose={() => setMessage('')} />
            <h2 className="font-display text-4xl sm:text-5xl text-gray-800 mb-8 text-center tracking-wider">Forms & Information</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-blue-50 p-6 rounded-lg shadow-sm border border-blue-100">
                    <h3 className="text-2xl font-bold text-blue-800 mb-4">Downloadable Forms</h3>
                     <div className="space-y-3 mb-8">
                        {forms.map(form => (
                            <div key={form.id} className="flex items-center justify-between bg-white p-4 rounded-md shadow-sm border border-gray-200">
                                <a href={form.url} target="_blank" rel="noreferrer" className="text-blue-600 font-semibold">{form.name}</a>
                                {userDetails?.isAdmin && <button onClick={() => handleDeleteForm(form)} className="text-red-500 text-xs">Delete</button>}
                            </div>
                        ))}
                    </div>
                    {userDetails?.isAdmin && (
                         <form onSubmit={handleUploadForm} className="space-y-3 mt-6">
                             <input type="text" value={uploadName} onChange={(e) => setUploadName(e.target.value)} placeholder="Form Name" className="w-full px-3 py-2 border rounded" required />
                             <input type="file" onChange={(e) => setUploadFile(e.target.files[0])} className="w-full text-sm" required />
                             <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-2 rounded">{loading ? 'Uploading...' : 'Upload'}</button>
                         </form>
                    )}
                </div>
                {/* Terms Section */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-sm border border-gray-200 flex flex-col">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-2xl font-bold text-gray-800">Terms & Conditions</h3>
                        {userDetails?.isAdmin && !isEditingTerms && <button onClick={handleEditTerms} className="bg-yellow-500 text-white px-3 py-1 rounded text-sm font-bold">Edit</button>}
                    </div>
                    <div className="flex-grow bg-white p-4 rounded border border-gray-300 overflow-y-auto max-h-[600px] text-sm leading-relaxed whitespace-pre-wrap">
                        {isEditingTerms ? (
                            <div className="h-full flex flex-col">
                                <textarea className="flex-grow w-full p-2 border border-blue-300 rounded mb-3" value={tempTermsText} onChange={(e) => setTempTermsText(e.target.value)} rows="12"></textarea>
                                <div className="mb-4"><input type="checkbox" checked={forceReset} onChange={(e) => setForceReset(e.target.checked)} /> Force Reset?</div>
                                <div className="flex space-x-3">
                                    <button onClick={handleSaveTerms} className="flex-1 bg-green-600 text-white py-2 rounded">Save</button>
                                    <button onClick={handleCancelEditTerms} className="flex-1 bg-gray-300">Cancel</button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-gray-700">{termsText || "Loading..."}</div>
                        )}
                    </div>
                </div>
            </div>
             <div className="mt-8 bg-green-50 p-6 rounded-lg shadow-sm border border-green-200">
                <h3 className="text-2xl font-bold text-green-800 mb-6 text-center">Banking Details</h3>
                <div className="text-center">
                    <p>Bank: {BANKING_DETAILS.bank}</p>
                    <p>Acc: {BANKING_DETAILS.accountNumber}</p>
                    <img src={BANKING_DETAILS.qrCodeUrl} alt="QR" className="mx-auto w-48 mt-4" />
                </div>
            </div>
        </div>
    );
}

export default FormsPage;
